import { ArrowDownIcon } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useRef } from 'react'

interface BPTimePickerProps {
  referenceTime?: string
  showDuration?: boolean
  value: string
  onChange: (value: string) => void
  onBlur: (e: React.FocusEvent<any>) => void
  errors?: string
}

// Generate time options at 15-minute intervals
const generateTimeOptions = (referenceTime?: string) => {
  const times = []
  let startTime: Date

  if (referenceTime) {
    // referenceTime is used when there is another time picker that is used to set the start time
    // to set end time options relative to the selected start time
    const normalizedReferenceTime = normalizeTimeValue(referenceTime)
    const referenceDate = new Date(`2000-01-01 ${normalizedReferenceTime}`)

    // Round to nearest 15-minute interval
    const minutes = referenceDate.getMinutes()
    const roundedMinutes = Math.round(minutes / 15) * 15
    const roundedDate = new Date(referenceDate)
    roundedDate.setMinutes(roundedMinutes)

    startTime = new Date(roundedDate.getTime() + 15 * 60 * 1000)
  } else {
    startTime = new Date('2000-01-01 00:00')
  }

  for (let i = 0; i < 96; i++) {
    // 96 = 24 hours * 4 (15-minute intervals)
    const time = new Date(startTime.getTime() + i * 15 * 60 * 1000)
    const timeString = `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`
    const displayTime = time
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
      .toLowerCase()
      .replace(' ', '')
    times.push({ value: timeString, label: displayTime })
  }

  return times
}

// Simple time normalization using Date object
const normalizeTimeValue = (timeValue: string): string => {
  if (!timeValue) return ''

  // If it's already in 24-hour format (HH:mm), return as is
  if (/^\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue
  }

  // Use Date to parse the time string
  const date = new Date(`2000-01-01 ${timeValue}`)
  if (isNaN(date.getTime())) return timeValue

  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
}

// Calculate duration between two times
const calculateDuration = (startTime: string, endTime: string): string => {
  const start = new Date(`2000-01-01 ${startTime}`)
  const end = new Date(`2000-01-01 ${endTime}`)

  // Handle case where end time is on the next day
  if (end < start) {
    end.setDate(end.getDate() + 1)
  }

  const diffMs = end.getTime() - start.getTime()
  const diffMinutes = Math.round(diffMs / (1000 * 60))

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  if (minutes === 0) {
    return `${hours}h`
  }

  return `${hours}h ${minutes}m`
}

export const BPTimePicker: React.FC<BPTimePickerProps> = ({
  referenceTime,
  value,
  onChange,
  onBlur,
  errors,
  showDuration = false
}) => {
  const timeOptions = useMemo(
    () => generateTimeOptions(referenceTime),
    [referenceTime]
  )
  const selectedTime = useMemo(() => {
    const normalizedValue = normalizeTimeValue(value)

    // First try to find exact match
    const exactMatch = timeOptions.find(
      option => option.value === normalizedValue
    )
    if (exactMatch) return exactMatch

    // If no exact match, find the next available option after the value
    if (normalizedValue) {
      const nextOption = timeOptions.find(
        option => option.value > normalizedValue
      )
      if (nextOption) return nextOption
    }

    // Fallback to first option if no next option found
    return timeOptions[0]
  }, [value, timeOptions])

  const selectedItemRef = useRef<HTMLButtonElement>(null)

  // Scroll to selected item when menu opens
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [])

  return (
    <Box width="88px">
      <Menu autoSelect={false} placement="bottom-start">
        <MenuButton
          borderBottom="1px solid rgba(87, 89, 91, 0.04)"
          p="0"
          m="0"
          h="48px"
          w="100%"
          bg="white"
          borderRadius="0"
          as={Button}
          rightIcon={<ArrowDownIcon />}
          _focus={{ borderBottom: 'solid 2px primary' }}
          isInvalid={!!errors}
          onBlur={onBlur}
        >
          <Text textAlign="left" fontSize="16px" color="black">
            {selectedTime?.label}
          </Text>
        </MenuButton>
        <MenuList
          overflowY="auto"
          w="100%"
          minWidth="unset"
          style={{ width: 'inherit' }}
          maxHeight="200px"
          zIndex={9999}
        >
          {timeOptions.map(option => (
            <MenuItem
              textAlign="left"
              key={option.value}
              onClick={() => onChange(option.value)}
              value={option.value}
              color="black"
              h="32px"
              w="100%"
              ref={
                option.value === normalizeTimeValue(value)
                  ? selectedItemRef
                  : undefined
              }
              icon={
                option.value === normalizeTimeValue(value) ? (
                  <Box w="8px" h="16px">
                    ✓
                  </Box>
                ) : (
                  <Box w="8px" h="16px" />
                )
              }
              bg={option.value === normalizeTimeValue(value) ? '#F8F8F8' : ''}
            >
              {option.label}{' '}
              {showDuration &&
                referenceTime &&
                `(${calculateDuration(normalizeTimeValue(referenceTime), option.value)})`}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  )
}
